import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, SearchSelect } from './FieldComponents';
import { fieldRecommendationError, fieldRecommendationsApi } from '../services/fieldRecommendationsApi';
import '../styles/FieldRecommendations.css';

const asId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
export const recommendationCaseTypeScope = (value) => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const text = String(value);
    if (!/^(0|[1-9]\d*)$/.test(text)) return null;
    const number = Number(text);
    return Number.isSafeInteger(number) && number <= 2147483647 ? number : null;
};
const isEnabled = (value) => value === true || value === 1 || value === '1';
const RECOMMENDATION_VALUE_TYPES = Object.freeze({
    8: 'Contact',
    10: 'Dropdown',
});

export const recommendationFieldSections = (field) => {
    let sections = field?.section_id;
    if (typeof sections === 'string') {
        try { sections = JSON.parse(sections); } catch { return []; }
    }
    return (Array.isArray(sections) ? sections : [sections])
        .filter((value) => (typeof value === 'string' && /^\d+$/.test(value)) || typeof value === 'number')
        .map(Number).filter((value) => Number.isSafeInteger(value) && value >= 0);
};

const fieldInSection = (field, sectionId) => {
    const sections = recommendationFieldSections(field);
    return Boolean(asId(sectionId)) && (sections.includes(0) || sections.includes(asId(sectionId)));
};

export const recommendationFieldOptions = (field) => {
    let options = field?.options;
    if (typeof options === 'string') {
        try { options = JSON.parse(options); } catch { return []; }
    }
    // This contract uses option positions, not tuple IDs or free-text labels.
    return Array.isArray(options) && options.every((value) => typeof value === 'string') ? options : [];
};

const emptyRule = () => ({
    name: '', enabled: true, source_section_id: '', source_contact_field_id: '', source_value_field_id: '',
    target_section_id: '', target_contact_field_id: '', target_value_field_id: '', value_map: {},
});

const FieldPicker = ({ label, value, options, onChange, disabled, id }) => (
    <div className="form-group">
        <label htmlFor={id}>{label}</label>
        <SearchSelect inputId={id} ariaLabel={label} ariaRequired value={value} options={options}
            placeholder={`Select ${label.toLowerCase()}`} onChange={onChange} disabled={disabled} />
    </div>
);

function RecommendationEditor({ rule, catalog, busy, onSave, onCancel, globalScope = false }) {
    const [draft, setDraft] = useState(() => rule?.id ? {
        ...emptyRule(), ...rule, enabled: isEnabled(rule.enabled),
        value_map: rule.needs_review ? {} : { ...rule.value_map },
    } : emptyRule());
    const [confirmed, setConfirmed] = useState(false);
    const [validation, setValidation] = useState('');
    const fields = catalog.fields || [];
    const sections = (catalog.sections || []).map((section) => ({ value: String(section.id), label: section.name }));
    const caseTypeNames = new Map((catalog.case_types || [])
        .filter((caseType) => asId(caseType?.id))
        .map((caseType) => [Number(caseType.id), caseType.name]));
    const find = (id) => fields.find((field) => String(field.id) === String(id));
    const choices = (sectionId, types, excludedIds = [], showType = false) => {
        const allowedTypes = new Set((Array.isArray(types) ? types : [types]).map(Number));
        const excluded = new Set((Array.isArray(excludedIds) ? excludedIds : [excludedIds])
            .filter((id) => id !== '' && id !== null && id !== undefined).map(String));
        return fields.filter((field) => {
            const type = Number(field.field_id);
            return allowedTypes.has(type) && fieldInSection(field, sectionId)
                && Number(field.add_item) === 1 && !excluded.has(String(field.id))
                && (type !== 10 || recommendationFieldOptions(field).length > 0);
        }).map((field) => {
            const fieldTypeLabel = showType ? ` (${RECOMMENDATION_VALUE_TYPES[Number(field.field_id)]})` : '';
            const fieldCaseTypeId = asId(field.case_type_id);
            const caseTypeLabel = globalScope && fieldCaseTypeId
                ? ` — ${caseTypeNames.get(fieldCaseTypeId) || `Case type ${fieldCaseTypeId}`}`
                : '';
            return {
                value: String(field.id),
                label: `${field.name}${fieldTypeLabel}${caseTypeLabel}`,
            };
        });
    };
    const sourceValueField = find(draft.source_value_field_id);
    const targetValueField = find(draft.target_value_field_id);
    const sourceValueType = Number(sourceValueField?.field_id || 0);
    const targetValueType = Number(targetValueField?.field_id || 0);
    const sourceOptions = recommendationFieldOptions(sourceValueField);
    const targetOptions = recommendationFieldOptions(targetValueField);
    const update = (name, value) => {
        setValidation('');
        setConfirmed(false);
        setDraft((current) => {
            const next = { ...current, [name]: value };
            if (name === 'source_section_id' || name === 'target_section_id') {
                const side = name.startsWith('source') ? 'source' : 'target';
                next[`${side}_contact_field_id`] = '';
                next[`${side}_value_field_id`] = '';
                if (side === 'source') next.target_value_field_id = '';
                next.value_map = {};
            } else if (name === 'source_value_field_id') {
                next.target_value_field_id = '';
                next.value_map = {};
            } else if (name === 'target_value_field_id') {
                next.value_map = {};
            }
            return next;
        });
    };
    const submit = (event) => {
        event.preventDefault();
        const selectedIds = ['source_contact_field_id', 'source_value_field_id', 'target_contact_field_id', 'target_value_field_id'];
        if (!draft.name.trim() || !asId(draft.source_section_id) || !asId(draft.target_section_id)
            || selectedIds.some((key) => !asId(draft[key]))) {
            setValidation('Name the rule and select both sections, match contact fields, and value fields.');
            return;
        }
        if (new Set(selectedIds.map((key) => Number(draft[key]))).size !== 4) {
            setValidation('Choose four different fields so the rule cannot recommend a value from itself.');
            return;
        }
        if (Number(draft.source_section_id) === Number(draft.target_section_id)) {
            setValidation('Choose different source and target sections.');
            return;
        }
        if (!RECOMMENDATION_VALUE_TYPES[sourceValueType] || sourceValueType !== targetValueType) {
            setValidation('Choose source and target value fields of the same supported type.');
            return;
        }
        const valueMap = {};
        if (sourceValueType === 10) {
            for (const [source, target] of Object.entries(draft.value_map)) {
                if (target === '' || target === null || target === undefined) continue;
                if (!/^\d+$/.test(source) || !/^\d+$/.test(String(target))
                    || Number(source) >= sourceOptions.length || Number(target) >= targetOptions.length) {
                    setValidation('A mapped option is no longer available. Review the dropdown mapping.');
                    return;
                }
                valueMap[source] = Number(target);
            }
            if (!Object.keys(valueMap).length) {
                setValidation('Map at least one source option to a recommended option.');
                return;
            }
        }
        if (rule?.needs_review && !confirmed) {
            setValidation('Review the current options and confirm the new mapping before saving.');
            return;
        }
        const fieldFingerprints = {};
        for (const key of selectedIds) {
            const field = find(draft[key]);
            const side = key.startsWith('source') ? 'source' : 'target';
            const type = key.includes('contact') ? 8 : sourceValueType;
            if (!field?.fingerprint || Number(field.field_id) !== type || Number(field.add_item) !== 1
                || !fieldInSection(field, draft[`${side}_section_id`])) {
                setValidation('A selected field is no longer available for this section. Reload the configuration.');
                return;
            }
            fieldFingerprints[String(field.id)] = field.fingerprint;
        }
        onSave({
            ...(rule?.id ? { id: rule.id, version: rule.version } : {}),
            name: draft.name.trim(), enabled: draft.enabled,
            source_section_id: Number(draft.source_section_id), target_section_id: Number(draft.target_section_id),
            ...Object.fromEntries(selectedIds.map((key) => [key, Number(draft[key])])),
            value_map: valueMap, field_fingerprints: fieldFingerprints,
            ...(rule?.needs_review ? { review_confirmed: true } : {}),
        });
    };
    return (
        <form className="field-recommendations-editor" onSubmit={submit} aria-label="Recommendation rule">
            <h4>{rule?.id ? 'Edit recommendation' : 'New recommendation'}</h4>
            {rule?.needs_review && <p role="alert" className="subtext">
                Field options or configuration changed. Rebuild the mapping below; the previous mapping will not be reused.
            </p>}
            <div className="field-recommendations-grid">
                <div className="form-group">
                    <label htmlFor="field-recommendation-name">Rule name</label>
                    <input id="field-recommendation-name" value={draft.name} maxLength={160} disabled={busy}
                        onChange={(event) => update('name', event.target.value)} />
                </div>
                <div className="form-group field-recommendations-toggle">
                    <Checkbox value={draft.enabled} onChange={() => update('enabled', !draft.enabled)}
                        disabled={busy} label="Enable this recommendation" ariaLabel="Enable this recommendation" />
                </div>
                <div className="field-recommendations-side">
                    <h4>Use a value from</h4>
                    <FieldPicker id="recommendation-source-section" label="Source section" value={draft.source_section_id}
                        options={sections} disabled={busy} onChange={(value) => update('source_section_id', value)} />
                    <FieldPicker id="recommendation-source-contact" label="Source match contact" value={draft.source_contact_field_id}
                        options={choices(draft.source_section_id, 8, [draft.target_contact_field_id, draft.source_value_field_id])} disabled={busy || !draft.source_section_id}
                        onChange={(value) => update('source_contact_field_id', value)} />
                    <FieldPicker id="recommendation-source-value" label="Source value field" value={draft.source_value_field_id}
                        options={choices(draft.source_section_id, [8, 10], [draft.source_contact_field_id, draft.target_value_field_id], true)} disabled={busy || !draft.source_section_id}
                        onChange={(value) => update('source_value_field_id', value)} />
                </div>
                <div className="field-recommendations-side">
                    <h4>Recommend it in</h4>
                    <FieldPicker id="recommendation-target-section" label="Target section" value={draft.target_section_id}
                        options={sections} disabled={busy} onChange={(value) => update('target_section_id', value)} />
                    <FieldPicker id="recommendation-target-contact" label="Target match contact" value={draft.target_contact_field_id}
                        options={choices(draft.target_section_id, 8, [draft.source_contact_field_id, draft.target_value_field_id])} disabled={busy || !draft.target_section_id}
                        onChange={(value) => update('target_contact_field_id', value)} />
                    <FieldPicker id="recommendation-target-value" label="Target value field" value={draft.target_value_field_id}
                        options={choices(draft.target_section_id, sourceValueType ? [sourceValueType] : [],
                            [draft.source_value_field_id, draft.target_contact_field_id], true)}
                        disabled={busy || !draft.target_section_id || !sourceValueType}
                        onChange={(value) => update('target_value_field_id', value)} />
                </div>
            </div>
            {sourceValueType === 10 && targetValueType === 10 && sourceOptions.length > 0 && targetOptions.length > 0 && <div className="field-recommendations-mapping">
                <h4>Dropdown mapping</h4>
                <p className="subtext">Choose a recommendation for each source value. Unmapped values do not produce a recommendation.</p>
                {sourceOptions.map((label, index) => <div className="form-group" key={index}>
                    <label htmlFor={`recommendation-map-${index}`}>{label} (option {index})</label>
                    <SearchSelect inputId={`recommendation-map-${index}`} ariaLabel={`Recommendation for ${label} (option ${index})`}
                        value={draft.value_map[String(index)] == null ? '' : String(draft.value_map[String(index)])} disabled={busy} placeholder="No recommendation"
                        options={[{ value: '__none__', label: 'No recommendation' }, ...targetOptions.map((target, targetIndex) => ({
                            value: String(targetIndex), label: `${target} (option ${targetIndex})`,
                        }))]}
                        onChange={(value) => {
                            setValidation(''); setConfirmed(false);
                            setDraft((current) => ({ ...current, value_map: { ...current.value_map, [String(index)]: value === '__none__' ? '' : value } }));
                        }} />
                </div>)}
            </div>}
            {rule?.needs_review && <div className="form-group">
                <Checkbox value={confirmed} onChange={() => setConfirmed((value) => !value)} disabled={busy}
                    label="I reviewed the current field options and mappings" ariaLabel="I reviewed the current field options and mappings" />
            </div>}
            {validation && <p role="alert">{validation}</p>}
            <div className="form-group field-recommendations-actions">
                <button type="submit" className="action" disabled={busy}>{busy ? 'Saving…' : 'Save recommendation'}</button>
                <button type="button" className="action quaternary" onClick={onCancel} disabled={busy}>Cancel</button>
            </div>
        </form>
    );
}

function FieldRecommendationSettings({ templateId }) {
    const [caseTypeId, setCaseTypeId] = useState('');
    const [caseTypes, setCaseTypes] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [disabling, setDisabling] = useState(null);
    const caseTypeScope = recommendationCaseTypeScope(caseTypeId);
    const scope = `${templateId || ''}/${caseTypeId}`;
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        const controller = new AbortController();
        setCatalog(null); setError(null); setLoading(Boolean(asId(templateId)));
        if (!asId(templateId)) return () => controller.abort();
        fieldRecommendationsApi.catalog(Number(templateId), recommendationCaseTypeScope(caseTypeId), { signal: controller.signal }).then((data) => {
            if (controller.signal.aborted) return;
            setCaseTypes(Array.isArray(data.case_types) ? data.case_types : []);
            setCatalog({ ...data, scope });
        }).catch((failure) => {
            if (!controller.signal.aborted) setError(failure);
        }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [templateId, caseTypeId, refresh, scope]);

    const current = catalog?.scope === scope ? catalog : null;
    const busy = saving || disabling !== null;
    const write = async (operation, successMessage) => {
        const startedScope = scope;
        setError(null); setMessage('');
        try {
            await operation();
            if (!mounted.current || scopeRef.current !== startedScope) return;
            setEditing(null); setDisabling(null); setMessage(successMessage); setRefresh((value) => value + 1);
        } catch (failure) {
            if (mounted.current && scopeRef.current === startedScope) setError(failure);
        } finally {
            if (mounted.current && scopeRef.current === startedScope) { setSaving(false); setDisabling(null); }
        }
    };
    const save = (rule) => {
        if (busy || !asId(templateId) || caseTypeScope === null) return;
        setSaving(true);
        write(() => fieldRecommendationsApi.saveRule({ ...rule, template_id: Number(templateId), case_type_id: caseTypeScope }), 'Recommendation saved.');
    };
    const disable = (rule) => {
        if (busy || !window.confirm(`Disable “${rule.name}”? Existing case values will not change.`)) return;
        setDisabling(rule.id);
        write(() => fieldRecommendationsApi.disableRule({
            id: rule.id, version: rule.version, template_id: Number(templateId), case_type_id: caseTypeScope,
        }), 'Recommendation disabled. Existing case values were not changed.');
    };
    const sectionName = (id) => current?.sections?.find((section) => String(section.id) === String(id))?.name || 'Unavailable section';
    const fieldName = (id) => current?.fields?.find((field) => String(field.id) === String(id))?.name || 'Unavailable field';

    return <section className="field-recommendations-settings" aria-label="Field recommendations">
        <p className="subtext">Recommend a dropdown or contact value when two repeated items refer to the same contact. Users choose whether to apply it; existing field definitions and case values are not changed by these settings.</p>
        {!asId(templateId) ? <p>Select a template above to configure recommendations.</p> : <>
            <div className="form-group field-recommendations-case-type">
                <label htmlFor="recommendation-case-type">Case type</label>
                <SearchSelect inputId="recommendation-case-type" ariaLabel="Case type" ariaRequired value={caseTypeId}
                    options={[
                        { value: '0', label: 'All applicable case types' },
                        ...caseTypes.filter((type) => asId(type.id)).map((type) => ({ value: String(type.id), label: type.name })),
                    ]}
                    placeholder="Select a case type or all applicable types" disabled={busy || (!caseTypes.length && loading)}
                    onChange={(value) => {
                        if (editing && !window.confirm('Discard the unsaved recommendation and change case type?')) return;
                        setEditing(null); setError(null); setMessage(''); setCaseTypeId(value);
                    }} />
            </div>
            {caseTypeScope === null && <p className="subtext">Choose one case type or all applicable case types.</p>}
            {caseTypeScope === 0 && <p className="subtext">This scope appears only on cases in this layout where all selected fields are available for that case type.</p>}
            {loading && <p role="status">Loading recommendations…</p>}
            {error && <div className="field-recommendations-error" role="alert">
                <p>{fieldRecommendationError(error)}</p>
                <button className="action-tiny quaternary" type="button" disabled={busy}
                    onClick={() => { setEditing(null); setRefresh((value) => value + 1); }}>Reload configuration</button>
            </div>}
            {message && <p role="status">{message}</p>}
            {caseTypeScope !== null && current && !loading && <>
                <div className="field-recommendations-heading">
                    <h4>Recommendations</h4>
                    <button type="button" className="action" disabled={busy || editing !== null}
                        onClick={() => { setMessage(''); setEditing({}); }}>Add recommendation</button>
                </div>
                {!current.rules?.length ? <p className="subtext">No recommendations for this case type yet.</p> : <div className="users-table-wrapper field-recommendations-table-wrap" role="region" aria-label="Recommendation rules" tabIndex={0}>
                    <table className="users-table field-recommendations-table" aria-label="Configured recommendations">
                        <thead><tr><th scope="col">Rule</th><th scope="col">Source</th><th scope="col">Target</th><th scope="col">Status</th><th scope="col">Actions</th></tr></thead>
                        <tbody>{current.rules.map((rule) => <tr className="user-row" key={rule.id}>
                            <td>{rule.name}</td>
                            <td><span>{sectionName(rule.source_section_id)}</span><small className="subtext">{fieldName(rule.source_contact_field_id)} · {fieldName(rule.source_value_field_id)}</small></td>
                            <td><span>{sectionName(rule.target_section_id)}</span><small className="subtext">{fieldName(rule.target_contact_field_id)} · {fieldName(rule.target_value_field_id)}</small></td>
                            <td>{rule.needs_review ? 'Needs review' : isEnabled(rule.enabled) ? 'Enabled' : 'Disabled'}</td>
                            <td><div className="field-recommendations-actions">
                                <button className="action-tiny quaternary" type="button" disabled={busy || editing !== null}
                                    aria-label={`${rule.needs_review ? 'Reconfigure' : 'Edit'} ${rule.name}`}
                                    onClick={() => { setMessage(''); setEditing(rule); }}>{rule.needs_review ? 'Reconfigure' : 'Edit'}</button>
                                {isEnabled(rule.enabled) && <button className="action-tiny quaternary" type="button" disabled={busy || editing !== null}
                                    aria-label={`Disable ${rule.name}`} onClick={() => disable(rule)}>{disabling === rule.id ? 'Disabling…' : 'Disable'}</button>}
                            </div></td>
                        </tr>)}</tbody>
                    </table>
                </div>}
                {editing && <RecommendationEditor key={`${scope}/${editing.id || 'new'}/${editing.version || 0}`}
                    rule={editing} catalog={current} busy={busy} globalScope={caseTypeScope === 0}
                    onSave={save} onCancel={() => { setEditing(null); setError(null); }} />}
            </>}
        </>}
    </section>;
}

// Changing the top-level template must discard the previous template's draft and requests.
export default function ScopedFieldRecommendationSettings({ templateId }) {
    return <FieldRecommendationSettings key={String(templateId || '')} templateId={templateId} />;
}
